import React from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Chip,
} from "@mui/material";
import useApiEp from "../hooks/useApiEp";

const AutoSelectJioTemplate = ({
  value,
  onChange,
  label = "Template",
  placeholder = "Select template",
  required = false,
  disabled = false,
  size = "small",
  variant = "outlined",
  helperText,
  error: propError,
  enableSearch = true,
  allowCustomValues = false,
  maxOptions,
  showStatus = true,
}) => {
  // API call to fetch templates
  const {
    data: templates,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
    error: templatesError,
  } = useApiEp(`/rcs/get-my-templates-select`);

  const getErrorMessage = () => {
    if (templatesError instanceof Error) {
      return templatesError.message;
    }
    if (typeof templatesError === "string") {
      return templatesError;
    }
    return "Failed to load templates";
  };

  const handleChange = (event, newValue) => {
    onChange(newValue);
  };

  const filterOptions = (options, state) => {
    if (!enableSearch) return options;

    const inputValue = state.inputValue.trim().toLowerCase();
    if (!inputValue) return options;

    return options.filter((option) =>
      option.name.toLowerCase().includes(inputValue)
    );
  };

  const getOptions = () => {
    let options = templates || [];
    if (maxOptions && options.length > maxOptions) {
      options = options.slice(0, maxOptions);
    }
    return options;
  };

  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    return option.name || `Template ${option.id}`;
  };

  const isOptionEqualToValue = (option, value) => {
    if (!option || !value) return false;
    if (typeof option === "string" || typeof value === "string") {
      return option === value;
    }
    return option.id === value.id;
  };

  const renderOption = (props, option) => (
    <Box component="li" {...props}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box>
          {option.name}
          <Box
            component="span"
            sx={{ color: "text.secondary", ml: 1, fontSize: "0.875rem" }}
          >
            (ID: {option.id})
          </Box>
        </Box>
        {showStatus && option.status && (
          <Chip
            label={option.status}
            size="small"
            color={
              option.status === "active"
                ? "success"
                : option.status === "inactive"
                ? "default"
                : option.status === "draft"
                ? "warning"
                : "default"
            }
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );

  if (isTemplatesError) {
    return (
      <Alert severity="error" sx={{ width: "100%" }}>
        {getErrorMessage()}
      </Alert>
    );
  }

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      options={getOptions()}
      loading={isTemplatesLoading}
      disabled={disabled || isTemplatesLoading}
      size={size}
      filterOptions={enableSearch ? filterOptions : undefined}
      freeSolo={allowCustomValues}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
          required={required}
          error={propError || isTemplatesError}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isTemplatesLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default AutoSelectJioTemplate;
